import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  PawPrint, QrCode, Eye, AlertTriangle, Edit2, Save, X, 
  Calendar, MapPin, Plus, Trash2, Camera 
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPet, setEditingPet] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [stats, setStats] = useState({});
  const [deletingPet, setDeletingPet] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);

  const handlePhotoUpload = async (petId, file) => {
    if (!file) return;
    setUploadingPhoto(petId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${BACKEND_URL}/api/pets/${petId}/photo`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setPets(pets.map(pet =>
        pet.pet_id === petId ? { ...pet, photo_url: response.data.photo_url } : pet
      ));
      toast.success('Photo updated');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(null);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchPets = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${BACKEND_URL}/api/pets/owner/list`, {
          withCredentials: true
        });
        setPets(response.data);
        
        // Fetch stats for each pet
        const statsPromises = response.data.map(async (pet) => {
          try {
            const statsRes = await axios.get(`${BACKEND_URL}/api/pets/${pet.pet_id}/stats`, {
              withCredentials: true
            });
            return { petId: pet.pet_id, stats: statsRes.data };
          } catch {
            return { petId: pet.pet_id, stats: { scan_count: 0, sighting_count: 0 } };
          }
        });
        
        const statsResults = await Promise.all(statsPromises);
        const statsMap = {};
        statsResults.forEach(({ petId, stats }) => {
          statsMap[petId] = stats;
        });
        setStats(statsMap);
      } catch (error) {
        toast.error('Failed to load pets');
      } finally {
        setLoading(false);
      }
    };
    fetchPets();
  }, [user]);

  const handleToggleLostMode = async (petId, currentStatus) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/pets/${petId}/toggle-lost`,
        {},
        { withCredentials: true }
      );
      
      setPets(pets.map(pet => 
        pet.pet_id === petId ? { ...pet, is_lost: response.data.is_lost } : pet
      ));
      
      toast.success(response.data.is_lost ? 'Lost mode activated' : 'Lost mode deactivated');
    } catch (error) {
      toast.error('Failed to toggle lost mode');
    }
  };

  const handleEditPet = (pet) => {
    setEditingPet(pet.pet_id);
    setEditForm({
      pet_name: pet.pet_name || '',
      breed: pet.breed || '',
      color: pet.color || '',
      age: pet.age || '',
      behaviour_notes: pet.behaviour_notes || '',
      medical_notes: pet.medical_notes || '',
      reward_offered: pet.reward_offered || '',
      vet_info: pet.vet_info || ''
    });
  };

  const handleSaveEdit = async (petId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/pets/${petId}`,
        editForm,
        { withCredentials: true }
      );
      
      setPets(pets.map(pet => 
        pet.pet_id === petId ? { ...pet, ...editForm } : pet
      ));
      
      setEditingPet(null);
      toast.success('Pet profile updated');
    } catch (error) {
      toast.error('Failed to update pet');
    }
  };

  const handleCancelEdit = () => {
    setEditingPet(null);
    setEditForm({});
  };

  const handleDeletePet = async (petId, petName) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/pets/${petId}`, {
        withCredentials: true
      });
      
      setPets(pets.filter(pet => pet.pet_id !== petId));
      setDeletingPet(null);
      toast.success(`${petName}'s profile has been deleted`);
    } catch (error) {
      toast.error('Failed to delete pet profile');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-20">
          <div className="max-w-6xl mx-auto px-4 flex items-center justify-center min-h-[50vh]">
            <div className="animate-pulse flex flex-col items-center">
              <PawPrint className="w-12 h-12 text-primary mb-4" />
              <p className="text-muted-foreground">Loading dashboard...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-20" data-testid="dashboard-page">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground">
                Welcome back, {user?.name}
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage your pet profiles and track tag activity
              </p>
            </div>
            <Button 
              onClick={() => navigate('/activate')}
              className="bg-primary hover:bg-primary/90"
              data-testid="add-pet-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Pet
            </Button>
          </div>

          {/* Pets Grid */}
          {pets.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-12 text-center">
              <PawPrint className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
                No pets yet
              </h2>
              <p className="text-muted-foreground mb-6">
                Activate your first SaferPaws tag to add a pet
              </p>
              <Button 
                onClick={() => navigate('/activate')}
                className="bg-primary hover:bg-primary/90"
              >
                Activate Tag
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {pets.map((pet) => {
                const petStats = stats[pet.pet_id] || { scan_count: 0, sighting_count: 0 };
                const isEditing = editingPet === pet.pet_id;
                const petPhotoUrl = pet.photo_url 
                  ? `${BACKEND_URL}${pet.photo_url}`
                  : 'https://images.unsplash.com/photo-1719260122537-01c5999f6a3f?w=200&h=200&fit=crop';

                return (
                  <div 
                    key={pet.pet_id}
                    className={`bg-card rounded-xl border ${pet.is_lost ? 'border-destructive/60 shadow-[0_0_20px_rgba(239,68,68,0.15)]' : 'border-border'} overflow-hidden`}
                    data-testid={`pet-card-${pet.pet_id}`}
                  >
                    {/* Lost Mode Banner */}
                    {pet.is_lost && (
                      <div className="bg-destructive text-destructive-foreground px-4 py-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">Lost Mode Active</span>
                      </div>
                    )}

                    <div className="p-6">
                      <div className="flex flex-col md:flex-row gap-6">
                        {/* Pet Photo */}
                        <div className="flex-shrink-0">
                          <div className="relative group">
                            <img 
                              src={petPhotoUrl}
                              alt={pet.pet_name}
                              className="w-24 h-24 rounded-xl object-cover"
                            />
                            <label
                              htmlFor={`photo-upload-${pet.pet_id}`}
                              className="absolute inset-0 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                              data-testid={`upload-photo-${pet.pet_id}`}
                            >
                              {uploadingPhoto === pet.pet_id ? (
                                <div className="text-white text-xs">Uploading…</div>
                              ) : (
                                <Camera className="w-6 h-6 text-white" />
                              )}
                            </label>
                            <input
                              id={`photo-upload-${pet.pet_id}`}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handlePhotoUpload(pet.pet_id, e.target.files[0])}
                              data-testid={`photo-input-${pet.pet_id}`}
                            />
                          </div>
                        </div>

                        {/* Pet Info */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <Label>Pet Name</Label>
                                  <Input
                                    value={editForm.pet_name}
                                    onChange={(e) => setEditForm({...editForm, pet_name: e.target.value})}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label>Breed</Label>
                                  <Input
                                    value={editForm.breed}
                                    onChange={(e) => setEditForm({...editForm, breed: e.target.value})}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label>Color</Label>
                                  <Input
                                    value={editForm.color}
                                    onChange={(e) => setEditForm({...editForm, color: e.target.value})}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label>Age</Label>
                                  <Input
                                    value={editForm.age}
                                    onChange={(e) => setEditForm({...editForm, age: e.target.value})}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Behaviour Notes</Label>
                                <Textarea
                                  value={editForm.behaviour_notes}
                                  onChange={(e) => setEditForm({...editForm, behaviour_notes: e.target.value})}
                                  className="mt-1"
                                  rows={2}
                                />
                              </div>
                              <div>
                                <Label>Reward Offered (if lost)</Label>
                                <Input
                                  value={editForm.reward_offered}
                                  onChange={(e) => setEditForm({...editForm, reward_offered: e.target.value})}
                                  placeholder="e.g., £100"
                                  className="mt-1"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  onClick={() => handleSaveEdit(pet.pet_id)}
                                  className="bg-primary hover:bg-primary/90"
                                  data-testid={`save-edit-${pet.pet_id}`}
                                >
                                  <Save className="w-4 h-4 mr-2" />
                                  Save
                                </Button>
                                <Button 
                                  variant="outline"
                                  onClick={handleCancelEdit}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="text-xl font-heading font-semibold text-foreground">
                                    {pet.pet_name}
                                  </h3>
                                  <p className="text-muted-foreground text-sm">
                                    {pet.breed || pet.pet_type} • {pet.color} • {pet.age}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditPet(pet)}
                                  data-testid={`edit-pet-${pet.pet_id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              </div>

                              {/* Stats */}
                              <div className="flex flex-wrap gap-4 mt-4">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <QrCode className="w-4 h-4" />
                                  <span>{petStats.scan_count} scans</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Eye className="w-4 h-4" />
                                  <span>{petStats.sighting_count} sightings</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Calendar className="w-4 h-4" />
                                  <span>ID: {pet.pet_id}</span>
                                </div>
                              </div>

                              {/* Profile Link */}
                              <div className="mt-4 p-3 bg-secondary rounded-lg">
                                <p className="text-xs text-muted-foreground mb-1">Profile URL</p>
                                <p className="text-sm font-mono text-primary truncate">
                                  {window.location.origin}/pet/{pet.pet_id}
                                </p>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Lost Mode Toggle */}
                        {!isEditing && (
                          <div className="flex flex-col items-center gap-2 border-l border-border pl-6">
                            <span className="text-sm font-medium text-foreground">Lost Mode</span>
                            <Switch
                              checked={pet.is_lost}
                              onCheckedChange={() => handleToggleLostMode(pet.pet_id, pet.is_lost)}
                              data-testid={`toggle-lost-${pet.pet_id}`}
                            />
                            <span className={`text-xs ${pet.is_lost ? 'text-destructive' : 'text-muted-foreground'}`}>
                              {pet.is_lost ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      {!isEditing && (
                        <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-border">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/pet/${pet.pet_id}`, '_blank')}
                            data-testid={`view-profile-${pet.pet_id}`}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Profile
                          </Button>
                          {pet.is_lost && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/pet/${pet.pet_id}/sightings`)}
                            >
                              <MapPin className="w-4 h-4 mr-2" />
                              View Sightings
                            </Button>
                          )}
                          
                          {/* Delete Button */}
                          {deletingPet === pet.pet_id ? (
                            <div className="flex items-center gap-2 ml-auto">
                              <span className="text-sm text-destructive">Delete {pet.pet_name}?</span>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeletePet(pet.pet_id, pet.pet_name)}
                                data-testid={`confirm-delete-${pet.pet_id}`}
                              >
                                Yes, Delete
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeletingPet(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-auto text-muted-foreground hover:text-destructive"
                              onClick={() => setDeletingPet(pet.pet_id)}
                              data-testid={`delete-pet-${pet.pet_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
